import { ApiError } from "@/lib/apiClient";
import {
  Tax,
  CreateTaxDto,
  UpdateTaxDto,
  FetchTaxesOptions,
  TaxFilters,
} from "@/types/models/tax";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStoreSpecificState } from "@/store/actions/storeActions";

export interface TaxesState {
  // Tax list state
  items: Tax[];
  loaded: boolean;
  loading: boolean;
  error: ApiError | null;
  pagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Single tax state
  currentTax: Tax | null;
  taxLoading: boolean;
  taxError: ApiError | null;

  // Search state
  searchResults: Tax[];
  searchLoading: boolean;
  searchError: ApiError | null;
  searchPagination: {
    page: number;
    total: number;
    pageSize: number;
    totalPages: number;
  };

  // Form state
  formState: {
    loading: boolean;
    error: ApiError | null;
    success: boolean;
  };

  // Filters
  filters: TaxFilters;
  fetchOptions: FetchTaxesOptions;
}

const initialState: TaxesState = {
  items: [],
  loaded: false,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  currentTax: null,
  taxLoading: false,
  taxError: null,

  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchPagination: {
    page: 1,
    total: 0,
    pageSize: 10,
    totalPages: 0,
  },

  formState: {
    loading: false,
    error: null,
    success: false,
  },

  filters: {},
  fetchOptions: {
    page: 1,
    page_size: 10,
    sort_by: "created_at",
    sort_order: "desc",
  },
};

const taxSlice = createSlice({
  name: "taxes",
  initialState,
  reducers: {
    // Fetch taxes
    fetchTaxesRequest: (state, action: PayloadAction<FetchTaxesOptions>) => {
      state.loading = true;
      state.error = null;
      state.fetchOptions = { ...state.fetchOptions, ...action.payload };
    },
    fetchTaxesSuccess: (
      state,
      action: PayloadAction<{ data: Tax[]; pagination: any }>,
    ) => {
      state.loading = false;
      state.loaded = true;
      state.items = action.payload.data;
      state.pagination = action.payload.pagination;
    },
    fetchTaxesFailure: (state, action: PayloadAction<ApiError>) => {
      state.loading = false;
      state.loaded = true;
      state.error = action.payload;
    },

    // Search taxes
    searchTaxesRequest: (
      state,
      _action: PayloadAction<{ query: string; options?: FetchTaxesOptions }>,
    ) => {
      state.searchLoading = true;
      state.searchError = null;
    },
    searchTaxesSuccess: (
      state,
      action: PayloadAction<{ data: Tax[]; pagination: any }>,
    ) => {
      state.searchLoading = false;
      state.searchResults = action.payload.data;
      state.searchPagination = action.payload.pagination;
    },
    searchTaxesFailure: (state, action: PayloadAction<ApiError>) => {
      state.searchLoading = false;
      state.searchError = action.payload;
    },

    // Get single tax
    fetchTaxDetailsRequest: (state, _action: PayloadAction<{ id: string }>) => {
      state.taxLoading = true;
      state.taxError = null;
    },
    fetchTaxDetailsSuccess: (state, action: PayloadAction<Tax>) => {
      state.taxLoading = false;
      state.currentTax = action.payload;
    },
    fetchTaxDetailsFailure: (state, action: PayloadAction<ApiError>) => {
      state.taxLoading = false;
      state.taxError = action.payload;
    },

    // Create tax
    createTaxRequest: (state, _action: PayloadAction<CreateTaxDto>) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    createTaxSuccess: (state, action: PayloadAction<Tax>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.items = [action.payload, ...state.items];
    },
    createTaxFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Update tax
    updateTaxRequest: (
      state,
      _action: PayloadAction<{ id: string; data: UpdateTaxDto }>,
    ) => {
      state.formState.loading = true;
      state.formState.error = null;
      state.formState.success = false;
    },
    updateTaxSuccess: (state, action: PayloadAction<Tax>) => {
      state.formState.loading = false;
      state.formState.success = true;
      state.currentTax = action.payload;

      const index = state.items.findIndex(
        (item) => item.id === action.payload.id,
      );
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    updateTaxFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Delete tax
    deleteTaxRequest: (state, _action: PayloadAction<{ id: string }>) => {
      state.formState.loading = true;
      state.formState.error = null;
    },
    deleteTaxSuccess: (state, action: PayloadAction<{ id: string }>) => {
      state.formState.loading = false;
      state.items = state.items.filter((item) => item.id !== action.payload.id);
    },
    deleteTaxFailure: (state, action: PayloadAction<ApiError>) => {
      state.formState.loading = false;
      state.formState.error = action.payload;
    },

    // Utility actions
    setFilters: (state, action: PayloadAction<TaxFilters>) => {
      state.filters = action.payload;
    },
    resetFormState: (state) => {
      state.formState = {
        loading: false,
        error: null,
        success: false,
      };
    },
    clearCurrentTax: (state) => {
      state.currentTax = null;
      state.taxError = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreSpecificState, () => initialState);
  },
});

export const {
  fetchTaxesRequest,
  fetchTaxesSuccess,
  fetchTaxesFailure,
  searchTaxesRequest,
  searchTaxesSuccess,
  searchTaxesFailure,
  fetchTaxDetailsRequest,
  fetchTaxDetailsSuccess,
  fetchTaxDetailsFailure,
  createTaxRequest,
  createTaxSuccess,
  createTaxFailure,
  updateTaxRequest,
  updateTaxSuccess,
  updateTaxFailure,
  deleteTaxRequest,
  deleteTaxSuccess,
  deleteTaxFailure,
  setFilters,
  resetFormState,
  clearCurrentTax,
} = taxSlice.actions;

export default taxSlice.reducer;
